import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Landscape } from '@/components/generals/landscape'
import { Reveal } from '@/components/generals/reveal'
import { SplitHeading } from '@/components/generals/split-heading'

interface PageHeroProps {
    eyebrow: string
    title: string
    subtitle?: ReactNode
    image?: string
    position?: string
    children?: ReactNode
    className?: string
}

/** Opening block for inner pages: landscape bleeding into black behind a centered headline. */
export const PageHero = ({ eyebrow, title, subtitle, image, position = 'center', children, className }: PageHeroProps) => {
    return (
        <section className={cn('relative isolate overflow-hidden pb-16 pt-36 sm:pb-20 sm:pt-44', className)}>
            {image && (
                <div aria-hidden='true' className='absolute inset-0 -z-10 opacity-80'>
                    <div className='absolute inset-0 animate-[fade-in_2s_var(--ease-cinema)_both]'>
                        <Landscape src={image} priority position={position} fade='none' />
                    </div>
                    <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0.25)_30%,rgba(0,0,0,0.55)_60%,#000_100%)]' />
                </div>
            )}

            <div className='shell flex flex-col items-center text-center'>
                <Eyebrow>
                    {eyebrow}
                </Eyebrow>

                <SplitHeading
                    as='h1'
                    trigger='mount'
                    delay={150}
                    text={title}
                    className='mt-7 max-w-4xl text-balance text-[2.6rem] font-normal leading-[1] tracking-[-0.045em] text-cream sm:text-6xl md:text-7xl'
                />

                {subtitle && (
                    <Reveal as='p' trigger='mount' delay={700} className='mt-7 max-w-xl text-pretty text-[15px] leading-relaxed text-cream/60 sm:text-base'>
                        {subtitle}
                    </Reveal>
                )}

                {children}
            </div>
        </section>
    )
}
